import axios from 'axios';

// Get API URL from environment variable or default to same origin (proxied by nginx)
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging and auth (if needed in future)
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// API service methods
export const apiService = {
  // Health check
  healthCheck: () => api.get('/api/health'),

  // Dashboard data
  getDashboardData: () => api.get('/api/dashboard'),

  // Test runs
  getTestRuns: (limit = 50, offset = 0) => 
    api.get('/api/test-runs', { params: { limit, offset } }),
  
  getTestRun: (id) => api.get(`/api/test-runs/${id}`),
  
  getLatestTestRun: () => api.get('/api/test-runs/latest'),
  
  triggerTestRun: (payload = undefined) => api.post('/api/test-runs/trigger', payload),
  
  getTaskStatus: (taskId) => api.get(`/api/test-runs/status/${taskId}`),

  cancelTestRun: (runId) => api.patch(`/api/test-runs/${runId}/cancel`),

  rerunFailedTests: (runId) => api.post(`/api/test-runs/${runId}/rerun-failed`),

  getTestRunBreakdown: (runId) => api.get(`/api/test-runs/${runId}/breakdown`),

  getTestRunJourneys: (runId) => api.get(`/api/test-runs/${runId}/journeys`),

  // Health checks
  getHealthChecks: (limit = 20, offset = 0) =>
    api.get('/api/health-checks', { params: { limit, offset } }),

  triggerHealthCheck: () => api.post('/api/health-checks/trigger'),

  getHealthCheckSchedule: () => api.get('/api/health-check-schedule'),
  updateHealthCheckSchedule: (data) => api.put('/api/health-check-schedule', data),

  // Page performance (real-browser load times)
  getPerformanceLatest: (profile) =>
    api.get('/api/performance/latest', { params: profile ? { profile } : {} }),

  getPerformanceTrend: (pageId, profile = 'desktop', limit = 30) =>
    api.get('/api/performance/trend', { params: { page_id: pageId, profile, limit } }),

  getPerformanceTrendAll: (profile = 'desktop', limit = 30) =>
    api.get('/api/performance/trend-all', { params: { profile, limit } }),

  getPerformanceSweeps: (limit = 20) =>
    api.get('/api/performance/sweeps', { params: { limit } }),

  getPerformanceWaterfall: (pageId, profile = 'desktop') =>
    api.get('/api/performance/waterfall', { params: { page_id: pageId, profile } }),

  triggerPerformanceSweep: () => api.post('/api/performance/trigger'),

  getPerformanceSchedule: () => api.get('/api/performance-schedule'),
  updatePerformanceSchedule: (data) => api.put('/api/performance-schedule', data),

  // Test metadata
  getTestMetadata: () => api.get('/api/test-metadata'),

  // Notification settings
  getNotificationSettings: () => api.get('/api/settings/notifications'),
  updateNotificationSettings: (settings) => api.put('/api/settings/notifications', settings),

  // Screenshots
  getScreenshotUrl: (path) => `${API_BASE_URL}/api/screenshots/${path}`,

  // Mobile app tests (Maestro)
  getMobileTestRuns: (params = {}) =>
    api.get('/api/mobile-test-runs', { params }),

  getMobileTestRun: (id) =>
    api.get(`/api/mobile-test-runs/${id}`),

  getMobileTestRunBreakdown: (id) =>
    api.get(`/api/mobile-test-runs/${id}/breakdown`),

  getMobileTestRunJourneys: (id) =>
    api.get(`/api/mobile-test-runs/${id}/journeys`),

  getMobileTestSummary: (params = {}) =>
    api.get('/api/mobile-test-runs/summary', { params }),

  getMobileTestTrend: (params = {}) =>
    api.get('/api/mobile-test-runs/trend', { params }),

  getDashboardDataV2: (params = {}) => api.get('/api/dashboard-v2', { params }),

  // Jira integration
  getJiraHealth: () => api.get('/api/jira/health'),
  getJiraSuggestion: (resultId) => api.get(`/api/test-results/${resultId}/jira-suggestion`),
  createJiraTicket: (resultId) => api.post(`/api/test-results/${resultId}/jira-ticket`),
  getJiraTickets: () => api.get('/api/jira-tickets'),
  syncJiraTickets: () => api.post('/api/jira-tickets/sync'),
  // Mobile (Maestro) — identified by run id + suite + test name (no DB row id)
  getMobileJiraSuggestion: (runId, suite, test) =>
    api.get(`/api/mobile-test-runs/${runId}/jira-suggestion`, { params: { suite, test } }),
  createMobileJiraTicket: (runId, suite, test) =>
    api.post(`/api/mobile-test-runs/${runId}/jira-ticket`, { suite, test }),

  // Test-case search + cross-run history
  searchTestCases: (q, source = 'all') =>
    api.get('/api/test-cases/search', { params: { q, source } }),
  getTestCaseHistory: (params) =>
    api.get('/api/test-cases/history', { params }),

  // Test schedules
  getSchedules: () => api.get('/api/schedules'),
  createSchedule: (data) => api.post('/api/schedules', data),
  updateSchedule: (id, data) => api.put(`/api/schedules/${id}`, data),
  deleteSchedule: (id) => api.delete(`/api/schedules/${id}`),
  runScheduleNow: (id) => api.post(`/api/schedules/${id}/run-now`),
};

// Export the configured axios instance as well for custom requests
export { api };

// Export the base URL for display purposes
export const getApiBaseUrl = () => API_BASE_URL;
